<template>
  <div class="admin-sections-page">
    <!-- Permanent from lg (1280px) up; below that it closes and opens over the page from the toolbar button -->
    <v-navigation-drawer
      v-model="drawer"
      mobile-breakpoint="lg"
      width="300"
      style="padding-bottom: var(--footer-height, 0px)"
    >
      <template #prepend>
        <div class="d-flex align-center ga-2 pa-2">
          <v-text-field
            v-model="search"
            density="compact"
            variant="outlined"
            placeholder="Rechercher"
            prepend-inner-icon="mdi-magnify"
            single-line
            hide-details
            clearable
          ></v-text-field>
          <v-btn
            icon="mdi-map-plus"
            size="small"
            color="primary"
            variant="tonal"
            title="Ajouter une section"
            :loading="isAddingSection"
            @click="addSection()"
          ></v-btn>
        </div>
        <div class="px-3 pb-1 text-label-small text-medium-emphasis">{{ filteredSections.length }} sections</div>
        <v-divider></v-divider>
      </template>
      <div v-if="search && !filteredSections.length" class="pa-4 text-body-small text-medium-emphasis">
        Aucune section trouvée.
      </div>
      <v-list density="compact" class="py-0">
        <template v-for="group in groupedSections" :key="group.region">
          <v-list-subheader sticky class="text-primary font-weight-bold">{{ group.region }}</v-list-subheader>
          <v-list-item
            v-for="section in group.sections"
            :key="section.id"
            :active="selectedSection === section"
            color="primary"
            @click="selectSection(section)"
          >
            <v-list-item-title class="text-body-small font-weight-medium">
              {{ section.river || "Nouvelle section" }}<span v-if="section.name"> — {{ section.name }}</span>
            </v-list-item-title>
            <v-list-item-subtitle class="d-flex align-center ga-1 text-label-small">
              <span class="mr-1">{{ formatSectionCotations(section) }}</span>
              <v-icon :color="getIconColor(section.video_info)" title="Vidéos disponibles" size="x-small">
                mdi-file-video
              </v-icon>
              <v-icon :color="getIconColor(section.topo_info)" title="Cartes topo disponibles" size="x-small">
                mdi-map-legend
              </v-icon>
              <v-icon :color="getIconColor(section.direction_info)" title="Directions disponibles" size="x-small">
                mdi-google-maps
              </v-icon>
              <v-icon :color="getIconColor(sectionHasCamera(section))" title="Caméra disponible" size="x-small">
                mdi-webcam
              </v-icon>
              <v-icon v-if="!section.published" color="red" title="Section non publiée" size="x-small">
                mdi-eye-off
              </v-icon>
              <v-icon v-if="section.free_access" color="green" title="Section gratuite" size="x-small">
                mdi-currency-usd-off
              </v-icon>
            </v-list-item-subtitle>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>

    <v-toolbar color="page-toolbar" density="compact">
      <template v-if="isDrawerCollapsible" #prepend>
        <v-btn icon="mdi-format-list-bulleted" title="Liste des sections" @click="drawer = !drawer"></v-btn>
      </template>
      <v-toolbar-title class="text-body-large"> Liste des sections <small>(Administration)</small> </v-toolbar-title>
    </v-toolbar>
    <v-container fluid class="pa-4">
      <v-empty-state
        v-if="!selectedSection"
        icon="mdi-map-marker-path"
        title="Aucune section sélectionnée"
        :text="
          isDrawerCollapsible
            ? 'Ouvrez la liste des sections avec le bouton en haut à gauche.'
            : 'Sélectionnez une section dans la liste de gauche.'
        "
      ></v-empty-state>
      <template v-else>
        <div class="d-flex align-center flex-wrap ga-2 mb-2">
          <h2 class="text-title-large">
            {{ selectedSection.river || "Nouvelle section" }}
            <span v-if="selectedSection.name"> — {{ selectedSection.name }}</span>
          </h2>
          <v-chip size="x-small" label variant="tonal">alias : {{ selectedSection.alias || "—" }}</v-chip>
          <v-chip size="x-small" label variant="tonal">id : {{ selectedSection.id }}</v-chip>
        </div>
        <v-tabs v-model="step" density="compact" color="primary">
          <v-tab :value="1" prepend-icon="mdi-information-outline">Info</v-tab>
          <v-tab :value="2" :disabled="!isAdded" prepend-icon="mdi-gauge">Station</v-tab>
          <v-tab :value="3" :disabled="!isAdded" prepend-icon="mdi-map-legend">Topo</v-tab>
          <v-tab :value="4" :disabled="!isAdded" prepend-icon="mdi-file-video">Vidéos</v-tab>
          <v-tab :value="5" :disabled="!isAdded" prepend-icon="mdi-google-maps">Maps</v-tab>
          <v-tab :value="6" :disabled="!isAdded" prepend-icon="mdi-webcam">Cam</v-tab>
        </v-tabs>
        <v-divider></v-divider>
        <!-- Compact fields in every tab, without the space reserved for (mostly empty) hint messages -->
        <v-defaults-provider :defaults="formDefaults">
          <v-tabs-window v-model="step">
            <v-tabs-window-item :value="1">
              <SectionGeneral
                :section="selectedSection"
                :reloadData="reloadData"
                @update="reloadData = $event"
                @deleted="removeSection"
              ></SectionGeneral>
            </v-tabs-window-item>
            <v-tabs-window-item :value="2">
              <SectionFlow :section="selectedSection" :reloadData="reloadData" @update="reloadData = $event"></SectionFlow>
            </v-tabs-window-item>
            <v-tabs-window-item :value="3">
              <SectionTopos
                :section="selectedSection"
                :reloadData="reloadData"
                @update="reloadData = $event"
              ></SectionTopos>
            </v-tabs-window-item>
            <v-tabs-window-item :value="4">
              <SectionVideos
                :section="selectedSection"
                :reloadData="reloadData"
                @update="reloadData = $event"
              ></SectionVideos>
            </v-tabs-window-item>
            <v-tabs-window-item :value="5">
              <SectionDirections
                :section="selectedSection"
                :reloadData="reloadData"
                @update="reloadData = $event"
              ></SectionDirections>
            </v-tabs-window-item>
            <v-tabs-window-item :value="6">
              <SectionCamera :key="selectedSection.id" :section="selectedSection"></SectionCamera>
            </v-tabs-window-item>
          </v-tabs-window>
        </v-defaults-provider>
      </template>
    </v-container>
  </div>
</template>

<script>
  import { RiversService } from "@/services/Rivers.service";
  import SectionsService from "@/store/modules/sections";
  import SectionGeneral from "@/components/pages/admin/SectionGeneral";
  import SectionFlow from "@/components/pages/admin/SectionFlow";
  import SectionTopos from "@/components/pages/admin/SectionTopos";
  import SectionVideos from "@/components/pages/admin/SectionVideos";
  import SectionDirections from "@/components/pages/admin/SectionDirections";
  import SectionCamera from "@/components/pages/admin/SectionCamera.vue";
  import { notify, notifyError } from "@/services/Notification.service";
  import { sectionHasCamera } from "@/utils/cameras";

  const compactField = { density: "compact", hideDetails: "auto" };

  function normalizeForSearch(value) {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }

  export default {
    components: {
      SectionGeneral,
      SectionFlow,
      SectionTopos,
      SectionVideos,
      SectionDirections,
      SectionCamera,
    },
    data() {
      return {
        selectedSection: null,
        step: 1,
        reloadData: false,
        localSections: [], // Add local sections array
        search: "",
        drawer: null,
        isAddingSection: false,
        formDefaults: {
          VTextField: compactField,
          VCombobox: compactField,
          VAutocomplete: compactField,
          VSelect: compactField,
          VNumberInput: compactField,
          VTextarea: compactField,
          VFileInput: compactField,
          VSwitch: { density: "compact", hideDetails: true, color: "primary" },
        },
      };
    },
    async created() {
      await this.getSections();
      this.reloadData = false;
    },
    methods: {
      async getSections() {
        try {
          // Try using a direct Firestore query if getSections is not available
          if (typeof SectionsService.actions.getSections === "function") {
            const sectionsData = await SectionsService.actions.getSections();
            this.localSections = sectionsData || [];
          } else {
            // Manual query as fallback
            const db = await import("@/firebase").then((module) => module.db);
            const snapshot = await db.collection("sections").orderBy("alias").get();
            this.localSections = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }));
          }
        } catch (error) {
          console.error("Error loading sections:", error);
          this.localSections = [];
        }
      },
      sectionHasCamera,
      getIconColor(isActive) {
        return RiversService.getSectionIconColor(isActive);
      },
      formatSectionCotations(section) {
        if (!section) {
          return "";
        }

        const levels = [section.class_level_min, section.class_level, section.class_level_max].filter(Boolean);
        return levels.join(" / ");
      },
      selectSection(section) {
        this.selectedSection = section;
        if (this.isDrawerCollapsible) {
          this.drawer = false;
        }
      },
      async addSection() {
        const newSection = {
          alias: "",
          name: "",
          river: "",
          region: "",
          published: false,
          class_level_min: "",
          class_level: "",
          class_level_max: "",
          min_sug_level: null,
          recommended_sug_level: null,
          max_sug_level: null,
          portage: "",
        };
        this.isAddingSection = true;
        try {
          const id = await SectionsService.actions.addSection(newSection);
          // The list is loaded once, so add the new section to it and open it for editing
          const section = { ...newSection, id };
          this.localSections.push(section);
          this.search = "";
          this.step = 1;
          this.selectSection(section);
          notify("Nouvelle section créée. Complétez l'onglet Info puis sauvegardez.");
        } catch (error) {
          console.error(error);
          notifyError("Impossible de créer la section. Veuillez réessayer.");
        } finally {
          this.isAddingSection = false;
        }
      },
      removeSection(section) {
        this.localSections = this.localSections.filter((item) => item.id !== section.id);
        this.selectedSection = null;
        notify("La section a été supprimée.");
      },
    },
    computed: {
      sections() {
        function regionRiverSection(a, b) {
          if (a.region < b.region) return -1;
          if (a.region > b.region) return 1;
          if (a.river < b.river) return -1;
          if (a.river > b.river) return 1;
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;

          return 0;
        }

        // Use localSections instead of trying to access store directly
        return [...this.localSections].sort(regionRiverSection);
      },
      isDrawerCollapsible() {
        return this.$vuetify.display.mdAndDown;
      },
      filteredSections() {
        // Every word must appear in one of the fields, e.g. "rouge seigneurie"
        const words = normalizeForSearch(this.search).split(/\s+/).filter(Boolean);
        if (!words.length) {
          return this.sections;
        }
        return this.sections.filter((section) => {
          const text = normalizeForSearch([section.region, section.river, section.name, section.alias].join(" "));
          return words.every((word) => text.includes(word));
        });
      },
      groupedSections() {
        const groups = [];
        for (const section of this.filteredSections) {
          const region = section.region || "Sans région";
          if (!groups.length || groups[groups.length - 1].region !== region) {
            groups.push({ region, sections: [] });
          }
          groups[groups.length - 1].sections.push(section);
        }
        return groups;
      },
      isAdded() {
        if (this.selectedSection.alias) {
          return true;
        } else {
          return false;
        }
      },
    },
    watch: {
      selectedSection: {
        deep: true,
        handler() {
          this.reloadData = true;
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  .admin-sections-page :deep(.fab-fixed-bottom-right) {
    z-index: 3000 !important;
  }

</style>
